import { CHIBAN_SEARCH_MAX_LENGTH } from "@/features/map/constants";
import { countGraphemes } from "@/features/map/utils/sanitizeSearchText";

import {
  MAP_URL_PARAM_SCHEMA,
  normalizeUrlParamText,
  type MapUrlParams,
} from "./schema";

/**
 * URL クエリ文字列を `MapUrlParams` に変換する純粋関数。
 *
 * - `MAP_URL_PARAM_SCHEMA`（schema.ts）の zod スキーマに従って各キーをパース・正規化する。
 * - 値の正規化（trim / 制御文字除去 / maxLength / 真偽値の厳密一致）はスキーマ側に集約される。
 * - 重複パラメータは `URLSearchParams.get()` の挙動どおり最初の値を採用する。
 * - 先頭の `?` の有無は両対応（`URLSearchParams` の標準挙動）。
 *
 * @param search - `window.location.search`
 * @returns スキーマから導出された型安全な `MapUrlParams` オブジェクト
 */
export const parseMapUrlParams = (search: string): MapUrlParams => {
  const params = new URLSearchParams(search);
  // スキーマのキーごとに生の値（未指定は空文字列）を取り出し、スキーマで正規化する。
  // キー追加は schema.ts の 1 箇所のみで完結する（ここは shape を辿るため変更不要）。
  const input: Record<string, string> = Object.fromEntries(
    Object.keys(MAP_URL_PARAM_SCHEMA.shape).map((key): [string, string] => [
      key,
      params.get(key) ?? "",
    ])
  );
  return MAP_URL_PARAM_SCHEMA.parse(input);
};

/**
 * `address` パラメータが上限（`CHIBAN_SEARCH_MAX_LENGTH`）超過で棄却されたか判定する。
 *
 * `parseMapUrlParams` は「空・未指定」と「長さ超過」をどちらも `null` に正規化して区別できないため、
 * ユーザー向けの警告通知（`toast.warning`）を出すかの判定用に別途提供する。
 * 正規化（制御文字除去 → NFC → trim）は schema と同一の `normalizeUrlParamText` を共有する。
 * 上限判定も schema（`nullableString`）と同じ見た目の文字数で行い、採用/棄却の境界を一致させる。
 *
 * @param search - `window.location.search`
 * @returns 正規化後の文字数（見た目の文字数）が上限を超えていれば `true`
 */
export const isAddressParamTooLong = (search: string): boolean => {
  const raw = new URLSearchParams(search).get("address") ?? "";
  return countGraphemes(normalizeUrlParamText(raw)) > CHIBAN_SEARCH_MAX_LENGTH;
};
